import React from 'react';
import { motion } from 'framer-motion';
import { Edit, Trash2, Star, Eye, Calendar, Tag } from 'lucide-react';
import LazyImage from '../LazyImage';

const DashboardCard = ({ 
  dashboard, 
  onEdit, 
  onDelete, 
  onView,
  showActions = true,
  className = '',
  viewMode = 'grid',
  isHighlighted = false
}) => {
  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(dashboard);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(dashboard.id || dashboard._id);
  };

  const handleView = () => {
    // Open dashboard in new window/tab
    const dashboardUrl = `/dashboard/${dashboard.slug}`;
    window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Compact list view
  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ 
          opacity: 1, 
          y: 0,
          scale: isHighlighted ? 1.02 : 1,
          boxShadow: isHighlighted ? '0 20px 25px -5px rgba(59, 130, 246, 0.3), 0 10px 10px -5px rgba(59, 130, 246, 0.1)' : undefined
        }}
        transition={{ duration: 0.3 }}
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border transition-all duration-300 hover:shadow-2xl ${
          isHighlighted 
            ? 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-500/20 dark:ring-blue-400/20' 
            : 'border-gray-200 dark:border-gray-700'
        } ${className}`}
      >
        <div className="flex items-center p-3 gap-4 w-full">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {/* Main Image */}
            <div className="relative w-12 h-12 flex-shrink-0">
              <LazyImage
                src={dashboard.mainImage}
                alt={dashboard.title}
                className="w-full h-full object-cover rounded-lg"
                fallbackSrc="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=48&h=48&fit=crop"
              />
              {/* Featured badge moved next to title */}
            </div>

            {/* Dashboard Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                  {dashboard.title}
                </h3>
                {dashboard.isFeatured && (
                  <Star size={16} fill="currentColor" className="text-yellow-500 flex-shrink-0" />
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons - Anchored to the right */}
          {showActions && (
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleEdit}
                className="p-2 text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 dark:hover:from-blue-900/20 dark:hover:to-blue-800/20 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                title="Edit Dashboard"
              >
                <Edit size={14} />
              </motion.button>
              

              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDelete}
                className="p-2 text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 dark:hover:from-red-900/20 dark:hover:to-red-800/20 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                title="Delete Dashboard"
              >
                <Trash2 size={14} />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleView}
                className="px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-xs font-medium flex items-center gap-1.5 shadow-lg hover:shadow-xl"
              >
                <Eye size={12} />
                View
              </motion.button>
            </div>
          )}

          {/* Public View Button */}
          {!showActions && (
            <div className="flex items-center flex-shrink-0 ml-auto">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleView}
                className="px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-xs font-medium flex items-center gap-1.5 shadow-lg hover:shadow-xl"
              >
                <Eye size={12} />
                View
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // Original grid view
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: 1, 
        y: 0,
        scale: isHighlighted ? 1.05 : 1,
        boxShadow: isHighlighted 
          ? '0 0 0 3px rgba(59, 130, 246, 0.5), 0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
      }}
      whileHover={{ y: -8, scale: isHighlighted ? 1.08 : 1.02 }}
      transition={{ duration: 0.3 }}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border transition-all duration-300 ${className} flex flex-col h-96 ${
        isHighlighted 
          ? 'border-blue-500 dark:border-blue-400' 
          : 'border-gray-200 dark:border-gray-700'
      } hover:shadow-2xl`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <LazyImage
          src={dashboard.mainImage}
          alt={dashboard.title}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          fallbackSrc="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop"
        />
        
        {/* Featured Badge */}
        {dashboard.isFeatured && (
          <div className="absolute top-3 left-3">
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 shadow-lg">
              <Star size={12} fill="currentColor" />
              Featured
            </div>
          </div>
        )}

        {/* Category Badge */}
        <div className="absolute top-3 right-3">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 shadow-lg">
            <Tag size={12} />
            {dashboard.category}
          </div>
        </div>

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            whileHover={{ opacity: 1, y: 0, scale: 1.05 }}
            onClick={handleView}
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 shadow-xl hover:from-blue-700 hover:to-blue-800"
          >
            <Eye size={16} />
            View Dashboard
          </motion.button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex-1 flex flex-col">
        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 line-clamp-2">
          {dashboard.title}
        </h3>

        {/* Meta Info */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
          <div className="flex items-center gap-1">
            <Calendar size={12} />
            <span>{formatDate(dashboard.createdDate || dashboard.createdAt)}</span>
          </div>
          {dashboard.updatedAt && (
            <span>Updated {formatDate(dashboard.updatedAt)}</span>
          )}
        </div>

        {/* Tools Used */}
        {dashboard.toolsUsed && dashboard.toolsUsed.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {dashboard.toolsUsed.slice(0, 4).map((tool, index) => (
              <span
                key={index}
                className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 text-blue-700 dark:text-blue-300 text-xs rounded-lg font-medium shadow-sm"
              >
                {tool}
              </span>
            ))}
            {dashboard.toolsUsed.length > 4 && (
              <span className="px-3 py-1.5 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-500 dark:text-gray-400 text-xs rounded-lg shadow-sm">
                +{dashboard.toolsUsed.length - 4} more
              </span>
            )}
          </div>
        )}

        {/* Spacer to push actions to bottom */}
        <div className="flex-1"></div>

        {/* Admin Actions */}
        {showActions && (
          <div className="pt-4 border-t border-gray-100 dark:border-gray-700 mt-auto">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleEdit}
                  className="px-3 py-2.5 text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 dark:hover:from-blue-900/20 dark:hover:to-blue-800/20 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                  title="Edit Dashboard"
                >
                  <Edit size={16} />
                </motion.button>
                

                
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDelete}
                  className="px-3 py-2.5 text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 dark:hover:from-red-900/20 dark:hover:to-red-800/20 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                  title="Delete Dashboard"
                >
                  <Trash2 size={16} />
                </motion.button>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleView}
                className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl"
              >
                <Eye size={14} />
                View
              </motion.button>
            </div>
          </div>
        )}

        {/* Public View Actions */}
        {!showActions && (
          <div className="pt-4 border-t border-gray-100 dark:border-gray-700 mt-auto">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleView}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
            >
              <Eye size={16} />
              View Dashboard
            </motion.button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default DashboardCard;